import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import DateRangeIcon from '@material-ui/icons/DateRange';
import LockIcon from '@material-ui/icons/Lock';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import MenuBookOutlinedIcon from '@material-ui/icons/MenuBookOutlined';
import styled from 'styled-components';

import { ActionIcon } from '@mummssoftware/common-ui';
import type { PatientChatResponseDetail } from '@mummssoftware/utils';

import { useShowSources } from '../../hooks/useShowSources';
import type { MaiaDateRange, MaiaView } from '../../types';
import { rangeFromStart, StartDateBounds } from '../../utils/dateRange';
import { formatRangeLabel } from '../../utils/formatters';
import defaults from '../../translations/en.json';
import { DetailSlider } from './DetailSlider';
import { StartDatePopover } from './StartDatePopover';

export type MaiaHeaderControlsProps = {
  view: MaiaView;
  smallWidget?: boolean;
  dateRange: MaiaDateRange;
  onDateRangeChange: (range: MaiaDateRange) => void;
  responseDetail: PatientChatResponseDetail;
  onResponseDetailChange: (detail: PatientChatResponseDetail) => void;
  onDone: () => void;
  isStarting?: boolean;
  /** A question is waiting for its answer: the slider locks until it arrives. */
  isSending?: boolean;
  /** Past view: the range that conversation was locked to, when known. */
  pastRange?: MaiaDateRange | null;
  /**
   * Set only for a patient referred more than 365 days ago: history shows the
   * start-date button. Chat and past always show their locked range.
   */
  startDateBounds?: StartDateBounds | null;
  /**
   * The header has no expand/close icons (MAIA Chats' patient dialog), so
   * nothing keeps Done off the header's right edge.
   */
  noHeaderIcons?: boolean;
};

const Root = styled.div<{ $endGap?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  min-width: 0;
  padding-right: ${(props) => (props.$endGap ? '8px' : '0')};
`;

const RangeButton = styled(Button)`
  && {
    height: 24px;
    min-width: 0;
    padding: 0 7px;
    font-size: 11px;
    line-height: 1;
    text-transform: none;
    white-space: nowrap;
    color: ${(props) => props.theme.palette.text.primary};
    border-color: ${(props) => props.theme.palette.divider};
  }
  && .MuiButton-startIcon {
    margin-right: 4px;
    margin-left: 0;
    color: ${(props) => props.theme.palette.primary.main};
  }
  && .MuiSvgIcon-root {
    font-size: 14px;
  }
`;

const LockedRange = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(props) =>
    props.theme.palette.text.readOnly || props.theme.palette.text.secondary};
  cursor: default;
  && .MuiSvgIcon-root {
    font-size: 13px;
  }
`;

const DoneButton = styled(Button)`
  && {
    height: 24px;
    min-width: 0;
    padding: 0 9px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    text-transform: none;
    white-space: nowrap;
  }
  && .MuiButton-startIcon {
    margin-right: 4px;
    margin-left: 0;
  }
  && .MuiSvgIcon-root {
    font-size: 14px;
  }
`;

/**
 * Expanded "Sources" switch, its label in Done's type: 12px bold, line-height 1.
 * Element selector for the label: inside the dashboard the MUI class names are
 * generated per app, so `.MuiFormControlLabel-label` cannot be relied on.
 * Structure: label > switch span + label span.
 */
const SourcesSwitch = styled(FormControlLabel)`
  && {
    height: 24px;
    margin: 0;
  }
  && > span:last-child {
    margin-left: 2px;
    font-family: ${(props) => props.theme.typography.button.fontFamily};
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    color: ${(props) => props.theme.palette.text.primary};
  }
`;

const iconStyle = { margin: 0, width: 22, height: 22 };
// centred on the title box, the + sits below the middle of the capital letters
const startIconStyle = { ...iconStyle, position: 'relative' as const, top: -2 };

type StartChatActionProps = {
  onStart: () => void;
  isStarting?: boolean;
};

/**
 * History's "+", right after the widget title (WidgetHeader's titleAction).
 * A spinner while the conversation starts.
 */
export const StartChatAction = ({
  onStart,
  isStarting,
}: StartChatActionProps) =>
  isStarting ? (
    <CircularProgress size={16} style={{ margin: '0 3px' }} />
  ) : (
    <ActionIcon
      Icon={AddIcon}
      title={defaults.newConversation}
      aria-label={defaults.newConversation}
      onClick={onStart}
      style={startIconStyle}
    />
  );

/**
 * Children of the shared dashboard WidgetHeader: date range, detail slider, Done.
 *
 * Why it looks the way it does:
 * - The mockup draws a 46px MAIA header, but the dashboard's WidgetHeader is 35px
 *   and shared by every widget (title, expand, close). We render into its
 *   children slot to stay consistent with the other widgets, so everything sits
 *   on one line: 24px controls, 11px text, no captions under the slider.
 * - Width budget in the tile is ~440px minus the title (~60px) and the header's
 *   own icons (~70px): date button ~120px + slider 84px + Done ~22px.
 *   Expanded, the slider grows to 110px and gains Brief/Verbose captions.
 * - Done is an icon in the tile but a labelled button when expanded, because the
 *   expanded header's own close toggle is also a checkmark icon.
 * - Below 400px (phone single-column layout) the slider is dropped first.
 *
 * View states: history = editable range ("+" is StartChatAction); chat = range locked,
 * slider locked while an answer is pending, + Done;
 * past = that conversation's range and Done; no slider, each answer shows its own
 * response length. Chat and past also get the sources toggle (an icon in the
 * tile, a labelled switch expanded); it only shows or hides each answer's
 * Sources section and is the same for every patient.
 */
export const MaiaHeaderControls = ({
  view,
  smallWidget,
  dateRange,
  onDateRangeChange,
  responseDetail,
  onResponseDetailChange,
  onDone,
  isStarting,
  isSending,
  pastRange,
  startDateBounds,
  noHeaderIcons,
}: MaiaHeaderControlsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Phone-width single-column layout: the slider is the first thing to go.
  const tooNarrowForSlider = useMediaQuery('(max-width:400px)');
  const inHistory = view === 'history';
  const inPast = view === 'past';
  // display only: the answers already carry their sources
  const [showSources, setShowSources] = useShowSources();

  const lockedLabel = inPast
    ? pastRange && formatRangeLabel(pastRange)
    : formatRangeLabel(dateRange);

  let done: React.ReactNode;
  if (smallWidget) {
    done = (
      <ActionIcon
        Icon={CheckIcon}
        title={defaults.done}
        aria-label={defaults.done}
        onClick={onDone}
        style={iconStyle}
      />
    );
  } else {
    // Expanded: the header's own close toggle is also a checkmark, so label this one.
    done = (
      <DoneButton
        size="small"
        variant="contained"
        color="primary"
        startIcon={<CheckIcon />}
        onClick={onDone}
      >
        {defaults.done}
      </DoneButton>
    );
  }

  const sourcesLabel = showSources
    ? defaults.hideSources
    : defaults.showSources;
  let sourcesToggle: React.ReactNode;
  if (smallWidget) {
    sourcesToggle = (
      <ActionIcon
        Icon={showSources ? MenuBookIcon : MenuBookOutlinedIcon}
        title={sourcesLabel}
        aria-label={defaults.showSources}
        aria-pressed={showSources}
        onClick={() => setShowSources(!showSources)}
        style={iconStyle}
        data-testid="maia-sources-toggle"
      />
    );
  } else {
    sourcesToggle = (
      <SourcesSwitch
        control={
          <Switch
            size="small"
            color="primary"
            checked={showSources}
            onChange={(event) => setShowSources(event.target.checked)}
            inputProps={{ 'aria-label': defaults.showSources }}
            data-testid="maia-sources-toggle"
          />
        }
        label={defaults.sourcesTitle}
      />
    );
  }

  return (
    <Root $endGap={noHeaderIcons} data-testid="maia-header-controls">
      {startDateBounds && inHistory ? (
        <>
          <RangeButton
            size="small"
            variant="outlined"
            startIcon={<DateRangeIcon />}
            aria-label={`${defaults.dateRangeTitle}, ${formatRangeLabel(
              dateRange,
            )}`}
            aria-haspopup="dialog"
            disabled={isStarting}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {formatRangeLabel(dateRange)}
          </RangeButton>
          <StartDatePopover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            value={dateRange.fromDate}
            bounds={startDateBounds}
            onApply={(start) => onDateRangeChange(rangeFromStart(start))}
            onClose={() => setAnchorEl(null)}
          />
        </>
      ) : null}
      {/* every chat is locked to its range; a past row only shows one it stored */}
      {!inHistory && lockedLabel ? (
        <Tooltip title={defaults.dateRangeLocked}>
          <LockedRange aria-label={defaults.dateRangeLocked}>
            <LockIcon fontSize="inherit" />
            <span>{lockedLabel}</span>
          </LockedRange>
        </Tooltip>
      ) : null}
      {/* read only: each answer shows the level it was asked with instead */}
      {!inPast && !(smallWidget && tooNarrowForSlider) ? (
        <DetailSlider
          value={responseDetail}
          onChange={onResponseDetailChange}
          // the level goes with each question, so it can change between them
          disabled={isSending}
          compact={smallWidget}
        />
      ) : null}
      {inHistory ? null : sourcesToggle}
      {inHistory ? null : done}
    </Root>
  );
};

export default MaiaHeaderControls;
